'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Link from 'next/link';
import {
  Loader2, AlertCircle, ArrowRightLeft, Download, Lock,
} from 'lucide-react';
import { getStockSplits, type StockSplit } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { useFeatures } from '@/context/SiteContext';

// ── Date helpers ──────────────────────────────────────────────────────────────

function toYMD(d: Date): string {
  return d.toISOString().split('T')[0];
}

function formatDisplayDate(dateStr: string): string {
  if (!dateStr) return '--';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-0">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-6 py-4 border-b border-default/50">
          <div className="w-24 h-4 bg-border-default rounded animate-pulse" />
          <div className="w-16 h-4 bg-border-default rounded animate-pulse" />
          <div className="w-16 h-4 bg-border-default rounded animate-pulse" />
          <div className="w-20 h-4 bg-border-default rounded animate-pulse" />
        </div>
      ))}
    </div>
  );
}

// ── CSV Export ─────────────────────────────────────────────────────────────────

function exportCsv(splits: StockSplit[], filename: string) {
  const header = 'Date,Symbol,Ratio,From Factor,To Factor,Type\n';
  const rows = splits.map((s) => {
    const type = s.toFactor > s.fromFactor ? 'Forward' : s.toFactor < s.fromFactor ? 'Reverse' : 'Even';
    return `${s.splitDate},${s.symbol},${s.ratio},${s.fromFactor},${s.toFactor},${type}`;
  }).join('\n');
  const blob = new Blob([header + rows], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Section Table ─────────────────────────────────────────────────────────────

function SplitsTable({ splits, title, emptyMessage }: { splits: StockSplit[]; title: string; emptyMessage: string }) {
  if (splits.length === 0) {
    return (
      <div className="bg-card-bg border border-default rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-6 py-4 border-b border-default">
          <ArrowRightLeft size={16} className="text-sky-400" />
          <span className="text-sm font-medium text-slate-200">{title}</span>
          <span className="text-xs text-fg-subtle">· 0 results</span>
        </div>
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <div className="w-12 h-12 rounded-full bg-card-bg border border-default flex items-center justify-center">
            <ArrowRightLeft size={20} className="text-slate-600" />
          </div>
          <p className="text-fg-muted text-sm">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card-bg border border-default rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-default">
        <ArrowRightLeft size={16} className="text-sky-400" />
        <span className="text-sm font-medium text-slate-200">{title}</span>
        <span className="text-xs text-fg-subtle">· {splits.length} result{splits.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-default bg-surface/40">
              <th className="text-left px-6 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Symbol</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Split Ratio</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Type</th>
            </tr>
          </thead>
          <tbody>
            {splits.map((s, idx) => {
              const isForward = s.toFactor > s.fromFactor;
              const isReverse = s.toFactor < s.fromFactor;
              return (
                <tr
                  key={`${s.symbol}-${s.splitDate}`}
                  className={cn(
                    'border-b border-default/50 transition-colors hover:bg-card-hover-bg',
                    idx % 2 === 0 ? 'bg-transparent' : 'bg-surface/20',
                  )}
                >
                  <td className="px-6 py-4 text-slate-300 font-mono text-sm">
                    {formatDisplayDate(s.splitDate)}
                  </td>
                  <td className="px-4 py-4">
                    <Link
                      href={`/stocks/${s.symbol}`}
                      className="font-bold text-sky-400 hover:text-sky-300 transition-colors"
                    >
                      {s.symbol}
                    </Link>
                  </td>
                  <td className="px-4 py-4">
                    <span className={cn(
                      'inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold',
                      isForward ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                      isReverse ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                      'bg-slate-700/50 text-slate-300',
                    )}>
                      {s.ratio}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={cn(
                      'inline-flex px-2 py-0.5 rounded text-xs font-medium',
                      isForward ? 'text-emerald-400' :
                      isReverse ? 'text-red-400' :
                      'text-fg-muted',
                    )}>
                      {isForward ? 'Forward Split' : isReverse ? 'Reverse Split' : 'Even Split'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-border-default/50">
        {splits.map((s) => {
          const isForward = s.toFactor > s.fromFactor;
          const isReverse = s.toFactor < s.fromFactor;
          return (
            <div key={`${s.symbol}-${s.splitDate}-m`} className="px-4 py-4 hover:bg-card-hover-bg transition-colors">
              <div className="flex items-center justify-between mb-2">
                <Link
                  href={`/stocks/${s.symbol}`}
                  className="font-bold text-sky-400 hover:text-sky-300 transition-colors text-sm"
                >
                  {s.symbol}
                </Link>
                <span className={cn(
                  'inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold',
                  isForward ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                  isReverse ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                  'bg-slate-700/50 text-slate-300',
                )}>
                  {s.ratio}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-fg-subtle">
                <span>{formatDisplayDate(s.splitDate)}</span>
                <span className={cn(
                  isForward ? 'text-emerald-400' : isReverse ? 'text-red-400' : 'text-fg-muted',
                )}>
                  {isForward ? 'Forward' : isReverse ? 'Reverse' : 'Even'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function StockSplitsCalendarPage() {
  const features = useFeatures();
  const [splits, setSplits] = useState<StockSplit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    // Fetch last 30 days + next 90 days
    const now = new Date();
    const from = toYMD(new Date(now.getTime() - 30 * 86_400_000));
    const to = toYMD(new Date(now.getTime() + 90 * 86_400_000));
    getStockSplits(from, to)
      .then((data) => {
        setSplits(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load stock split data.');
        setLoading(false);
      });
  }, []);

  const todayStr = useMemo(() => toYMD(new Date()), []);

  const upcoming = useMemo(() => {
    return splits
      .filter((s) => s.splitDate >= todayStr)
      .sort((a, b) => a.splitDate.localeCompare(b.splitDate));
  }, [splits, todayStr]);

  const recent = useMemo(() => {
    return splits
      .filter((s) => s.splitDate < todayStr)
      .sort((a, b) => b.splitDate.localeCompare(a.splitDate));
  }, [splits, todayStr]);

  const handleExport = useCallback(() => {
    exportCsv([...upcoming, ...recent], 'stock-splits.csv');
  }, [upcoming, recent]);

  return (
    <div className="min-h-screen bg-surface text-white">
      <div className="px-2 sm:px-3 lg:px-4 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
                <ArrowRightLeft size={18} className="text-sky-400" />
              </div>
              <h1 className="text-2xl font-bold text-white">Stock Splits Calendar</h1>
            </div>
            <p className="text-fg-muted text-sm ml-12">
              Upcoming and recent stock split events
            </p>
          </div>

          {features.exportData ? (
            <button
              onClick={handleExport}
              disabled={splits.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-card-bg border border-default rounded-lg text-slate-200 hover:bg-card-hover-bg hover:border-sky-500/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download size={14} />
              Export CSV
            </button>
          ) : (
            <div className="relative group">
              <button disabled className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-card-bg border border-default rounded-lg text-slate-600 cursor-not-allowed">
                <Lock size={12} className="text-amber-500/70" /> Export CSV
              </button>
              <div className="absolute right-0 top-full mt-1.5 w-48 bg-card-bg border border-default rounded-lg px-3 py-2 text-xs text-fg-muted shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                <span className="text-amber-400 font-medium">Pro feature</span> — upgrade to unlock CSV export.
              </div>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl mb-6 text-red-400">
            <AlertCircle size={18} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="bg-card-bg border border-default rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-4 border-b border-default">
              <Loader2 size={16} className="text-sky-400 animate-spin" />
              <span className="text-sm font-medium text-slate-200">Loading splits...</span>
            </div>
            <LoadingSkeleton />
          </div>
        ) : (
          <div className="space-y-8">
            <SplitsTable
              splits={upcoming}
              title="Upcoming Splits"
              emptyMessage="No upcoming stock splits scheduled"
            />
            <SplitsTable
              splits={recent}
              title="Recent Splits"
              emptyMessage="No recent stock splits in the last 30 days"
            />
          </div>
        )}

        <div className="mt-6 text-xs text-slate-600">
          Stock split dates are estimates and subject to change.
        </div>
      </div>
    </div>
  );
}
